import {grey} from "@mui/material/colors";
import {DoorLiteDetail, GlazingOpeningRef, GlazingPanel} from "../../../../api/ElevationTool";
import {ContainerPage} from "../../../../misc/pdf/parts/ContainerPage";
import {TitleTextBlock} from "../../../../misc/pdf/parts/TitleTextBlock";
import {pdfTableStyle} from "../../../../misc/pdf/parts/Page";
import {BeadProfiles} from "./BeadProfiles";

// one section of a submittal: an elevation variant's drawing, the glass it needs and the openings
// built to it. Screens list panels (the frame grid's lites); doors list the lites cut into the leaf.
export type GlazingSection = {
    projectName: string;
    heading: string;
    image: string;
    panels?: GlazingPanel[];
    lites?: DoorLiteDetail[];
    openings: GlazingOpeningRef[];
};

// one elevation variant on its own page: the drawing on the left, the glass table and the
// openings that take it on the right.
export function GlazingSectionPage(props: {title: string; section: GlazingSection}) {
    return (
        <ContainerPage>
            <TitleTextBlock title={props.title}>
                <GlazingSectionBody section={props.section} />
            </TitleTextBlock>
        </ContainerPage>
    );
}

// the section's content without a page around it, so the purchase order PDF can print it under
// its own header. Fills the height it is given.
export function GlazingSectionBody(props: {section: GlazingSection; heading?: string}) {
    const section = props.section;

    return (
        <>
        <div style={{fontWeight: "bold", fontSize: "1rem", paddingBottom: 8}}>{props.heading ?? section.heading}</div>
        <div style={{flex: 1, display: "flex", flexDirection: "row", minHeight: 0}}>
            <div style={{
                flex: 2,
                display: "flex", flexDirection: "column",
                paddingRight: 8,
            }}>
                <div
                    data-pdf-image-high-res="true"
                    style={{
                        flex: 1,
                        minHeight: 0,
                        border: "1px solid " + grey["200"],
                        backgroundImage: "url(" + section.image + ")",
                        backgroundSize: "contain",
                        backgroundPosition: "center center",
                        backgroundRepeat: "no-repeat",
                    }}
                />
            </div>
            <div style={{flex: 3, paddingLeft: 8, display: "flex", flexDirection: "column"}}>
                {section.panels && <PanelTable panels={section.panels} />}
                {section.lites && <LiteTable lites={section.lites} />}
                <div style={{height: 16}} />
                <OpeningTable openings={section.openings} />
                <div style={{height: 16}} />
                <BeadProfiles beadKeys={sectionBeadKeys(section)} />
            </div>
        </div>
        </>
    );
}

// the beads the section's glass is held in, in the order the table lists them (BeadProfiles
// de-duplicates).
function sectionBeadKeys(section: GlazingSection): string[] {
    return [
        ...(section.panels || []).map(p => p.beadKey),
        ...(section.lites || []).map(l => l.beadKey),
    ];
}

// the frame grid's glass. A double-glazed lite is listed twice — once per pane — so the row
// count is the panel count to order. An elevation holding no glass has no section at all (the
// endpoint leaves it out), so this always has rows.
function PanelTable(props: {panels: GlazingPanel[]}) {
    return (
        <table className={pdfTableStyle} style={{fontSize: "0.6rem"}}>
            <thead>
                <tr>
                    <th>P#</th>
                    <th>Alignment</th>
                    <th>Thickness</th>
                    <th>Kind</th>
                    <th>Bead</th>
                    <th>Thermal</th>
                    <th>Nominal Size</th>
                    <th>Actual Size</th>
                </tr>
            </thead>
            <tbody>
                {props.panels.map((panel, index) => (
                    <tr key={index.toString()}>
                        <td>{panel.mark}</td>
                        <td>{panel.alignment}</td>
                        <td>{panel.thickness}</td>
                        <td>{panel.kind}</td>
                        <td>{panel.bead}</td>
                        <td>{panel.thermal}</td>
                        <td>{panel.nominalSize}</td>
                        <td>{panel.actualSize}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// the lites cut into a door leaf.
function LiteTable(props: {lites: DoorLiteDetail[]}) {
    return (
        <table className={pdfTableStyle} style={{fontSize: "0.6rem"}}>
            <thead>
                <tr>
                    <th>Mark</th>
                    <th>Cut-out</th>
                    <th>Glass Size</th>
                    <th>Type</th>
                    <th>Thermal</th>
                    <th>Thickness</th>
                    <th>Bead</th>
                </tr>
            </thead>
            <tbody>
                {props.lites.map(lite => (
                    <tr key={lite.mark}>
                        <td>{lite.mark}</td>
                        <td>{lite.cutOut}</td>
                        <td>{lite.glassSize}</td>
                        <td>{lite.type}</td>
                        <td>{lite.thermal}</td>
                        <td>{lite.thickness}</td>
                        <td>{lite.bead}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// the openings this glass table covers, so the glazier can see how many of each go out.
function OpeningTable(props: {openings: GlazingOpeningRef[]}) {
    return (
        <>
            <div style={{fontWeight: "bold", fontSize: "0.7rem", paddingBottom: 4}}>Openings</div>
            <table className={pdfTableStyle} style={{fontSize: "0.6rem"}}>
                <thead>
                    <tr>
                        <th>Opening</th>
                        <th style={{textAlign: "right"}}>Qty</th>
                    </tr>
                </thead>
                <tbody>
                    {props.openings.map(o => (
                        <tr key={o.id}>
                            <td>{o.name}</td>
                            <td style={{textAlign: "right"}}>{o.qty}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}
